import { apiClient } from '../api/apiClient';
import { API_ENDPOINTS } from '../api/apiEndpoints';
import type {
  OrganizationDetails,
  OrganizationUpdateInput,
} from '../types/organization';
import { getMasterDataStatusLabel } from '../utils/masterDataStatus';

type Envelope<T> = { success: boolean; message: string; data: T };
type ObjectValue = Record<string, unknown>;

function numberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function requiredId(value: unknown, label: string): number {
  const parsed = numberOrNull(value);
  if (parsed === null || parsed <= 0) {
    throw Object.assign(new Error(`${label} is missing`), {
      code: 'VALIDATION_ERROR',
    });
  }
  return parsed;
}

function textOrNull(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

function requiredText(value: unknown, label: string): string {
  const text = textOrNull(value);
  if (!text) {
    throw Object.assign(new Error(`${label} is required`), {
      code: 'VALIDATION_ERROR',
    });
  }
  return text;
}

function boolOrNull(value: unknown): boolean | null {
  if (value === true || value === 1 || value === '1') return true;
  if (value === false || value === 0 || value === '0') return false;
  return null;
}

function emptyToNull(value: string | null | undefined): string | null {
  if (value === undefined || value === null) return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

export function normalizeOrganization(row: ObjectValue): OrganizationDetails {
  const isActive = boolOrNull(row.isActive);
  const statusCode = textOrNull(row.statusCode) ?? textOrNull(row.statusName);
  return {
    organizationId: requiredId(row.organizationId, 'organizationId'),
    organizationCode: textOrNull(row.organizationCode),
    organizationName: requiredText(row.organizationName, 'organizationName'),
    legalName: textOrNull(row.legalName),
    registrationNumber: textOrNull(row.registrationNumber),
    taxRegistrationNumber: textOrNull(row.taxRegistrationNumber),
    officialEmail: textOrNull(row.officialEmail),
    phoneNumber: textOrNull(row.phoneNumber),
    websiteUrl: textOrNull(row.websiteUrl),
    addressLine1: textOrNull(row.addressLine1),
    addressLine2: textOrNull(row.addressLine2),
    city: textOrNull(row.city),
    stateName: textOrNull(row.stateName),
    countryCode: textOrNull(row.countryCode) ?? '',
    postalCode: textOrNull(row.postalCode),
    timeZone: textOrNull(row.timeZone) ?? '',
    defaultCurrencyCode: textOrNull(row.defaultCurrencyCode) ?? '',
    defaultDateFormat: textOrNull(row.defaultDateFormat) ?? '',
    defaultTimeFormat: textOrNull(row.defaultTimeFormat) ?? '',
    logoUrl: textOrNull(row.logoUrl),
    statusId: numberOrNull(row.statusId) ?? 1,
    statusCode,
    isActive,
    rowVersion: requiredId(row.rowVersion, 'rowVersion'),
  };
}

export function organizationStatusLabel(org: OrganizationDetails): string {
  return getMasterDataStatusLabel(org.isActive, org.statusCode);
}

/** GET /organization — current tenant only; do not send organizationId. */
export async function getCurrentOrganization(): Promise<OrganizationDetails> {
  const response = await apiClient.get<Envelope<ObjectValue>>(
    API_ENDPOINTS.ORGANIZATION.CURRENT,
  );
  return normalizeOrganization(response.data.data ?? {});
}

/**
 * PUT /organization — sends only validator-supported fields.
 * organizationId / audit fields are never sent.
 */
export async function updateCurrentOrganization(
  input: OrganizationUpdateInput,
): Promise<OrganizationDetails> {
  const body = {
    organizationName: input.organizationName.trim(),
    legalName: emptyToNull(input.legalName),
    registrationNumber: emptyToNull(input.registrationNumber),
    taxRegistrationNumber: emptyToNull(input.taxRegistrationNumber),
    officialEmail: emptyToNull(input.officialEmail),
    phoneNumber: emptyToNull(input.phoneNumber),
    websiteUrl: emptyToNull(input.websiteUrl),
    addressLine1: emptyToNull(input.addressLine1),
    addressLine2: emptyToNull(input.addressLine2),
    city: emptyToNull(input.city),
    stateName: emptyToNull(input.stateName),
    countryCode: input.countryCode.trim().toUpperCase(),
    postalCode: emptyToNull(input.postalCode),
    timeZone: input.timeZone.trim(),
    defaultCurrencyCode: input.defaultCurrencyCode.trim().toUpperCase(),
    defaultDateFormat: input.defaultDateFormat.trim(),
    defaultTimeFormat: input.defaultTimeFormat.trim(),
    logoUrl: emptyToNull(input.logoUrl),
    statusId: input.statusId,
    rowVersion: input.rowVersion,
  };

  const response = await apiClient.put<Envelope<ObjectValue>>(
    API_ENDPOINTS.ORGANIZATION.UPDATE_CURRENT,
    body,
  );
  return normalizeOrganization(response.data.data ?? {});
}
